import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, Building2 } from "lucide-react";
import { BankAccount } from "@/types/bank-statement";

interface BankAccountSelectorProps {
  accounts: BankAccount[];
  selectedAccountId: string;
  onAccountChange: (accountId: string) => void;
}

function BankLogo({ logoUrl, name }: { logoUrl: string | null; name: string }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="w-10 h-10 rounded-[14px] object-contain bg-white shrink-0"
      />
    );
  }

  return (
    <div className="w-10 h-10 rounded-[14px] bg-[#7846ff] flex items-center justify-center shrink-0">
      <Building2 className="w-5 h-5 text-white" />
    </div>
  );
}

export function BankAccountSelector({
  accounts,
  selectedAccountId,
  onAccountChange,
}: BankAccountSelectorProps) {
  const selectedAccount = accounts.find((a) => a.id.toString() === selectedAccountId);

  return (
    <Select value={selectedAccountId} onValueChange={onAccountChange}>
      <SelectTrigger className="border-[#5d5d5d] bg-transparent h-[50px] w-[342px] px-3 py-2 gap-3 rounded-[10px]">
        {selectedAccount ? (
          <div className="flex items-center gap-3 w-full">
            <BankLogo logoUrl={selectedAccount.bankLogoUrl} name={selectedAccount.bankName} />
            <div className="flex flex-col text-left flex-1 min-w-0">
              <p className="text-sm font-bold text-[#f6f6f6] leading-normal truncate">
                {selectedAccount.bankName}{selectedAccount.bankCode ? ` - ${selectedAccount.bankCode}` : ""}
              </p>
              <p className="text-xs font-normal text-[#f6f6f6] leading-normal truncate">
                Conta {selectedAccount.accountNumber}{selectedAccount.agency ? ` | Ag ${selectedAccount.agency}` : ""}
              </p>
            </div>
          </div>
        ) : (
          <SelectValue placeholder="Selecione uma conta" />
        )}
        <ChevronDown className="h-4 w-4 text-white ml-auto flex-shrink-0 transition-transform group-data-[state=open]:rotate-180" />
      </SelectTrigger>

      <SelectContent>
        {accounts.map((account) => (
          <SelectItem key={account.id} value={account.id.toString()}>
            <div className="flex items-center gap-3">
              <BankLogo logoUrl={account.bankLogoUrl} name={account.bankName} />
              <div>
                <p className="font-semibold text-white">
                  {account.bankName}{account.bankCode ? ` - ${account.bankCode}` : ""}
                </p>
                <p className="text-sm text-gray-400">
                  Conta {account.accountNumber}{account.agency ? ` | Ag ${account.agency}` : ""}
                </p>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

BankAccountSelector.displayName = "BankAccountSelector";
